/**
 * API-адаптер для RichEditor у Replast.
 * AI rewrite + upload на /api/lms/
 */
import { lmsService } from '../../services/api';

function errDetail(e, fallback) {
  return e?.response?.data?.detail || e?.message || fallback;
}

/** @param {string} text @param {string} action @param {string} [custom] */
export async function aiRewrite(text, action, custom = '') {
  try {
    const res = await lmsService.aiRewrite({ text, action, custom });
    const html = res.data?.html;
    if (!html) throw new Error('Порожня відповідь AI');
    return { html };
  } catch (e) {
    const err = new Error(errDetail(e, 'AI недоступний'));
    err.code = e?.response?.status === 503 ? 'AI_UNAVAILABLE' : 'AI_ERROR';
    throw err;
  }
}

export async function uploadFile(file) {
  if (!file) throw new Error('Немає файлу');
  // Дрібні зображення (<1.5 МБ) можна тимчасово як data URL, якщо API впаде
  const allowDataFallback = file.type?.startsWith('image/') && file.size < 1.5 * 1024 * 1024;
  try {
    const res = await lmsService.upload(file);
    return { url: res.data.url, name: res.data.name || file.name || 'file' };
  } catch (e) {
    if (!allowDataFallback) {
      throw new Error(errDetail(e, 'Не вдалося завантажити файл на сервер'));
    }
    const url = await readAsDataUrl(file);
    return { url, name: file.name || 'file', localFallback: true };
  }
}

export function uploadFileProgress(file, onProgress) {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('Немає файлу'));
      return;
    }
    const allowDataFallback = file.type?.startsWith('image/') && file.size < 1.5 * 1024 * 1024;
    lmsService
      .upload(file, {
        onUploadProgress: (ev) => {
          if (onProgress && ev.total) {
            onProgress(Math.round((ev.loaded / ev.total) * 100));
          }
        },
      })
      .then((res) => {
        if (onProgress) onProgress(100);
        resolve({ url: res.data.url, name: res.data.name || file.name || 'file' });
      })
      .catch(async (e) => {
        if (!allowDataFallback) {
          reject(new Error(errDetail(e, 'Не вдалося завантажити файл на сервер')));
          return;
        }
        try {
          const url = await readAsDataUrl(file);
          if (onProgress) onProgress(100);
          resolve({ url, name: file.name || 'file', localFallback: true });
        } catch (e2) {
          reject(e2);
        }
      });
  });
}

/** Зовнішній URL → проксі на сервер (або як є при помилці). */
export async function uploadUrl(url) {
  const u = String(url || '').trim();
  if (!u) throw new Error('Порожній URL');
  try {
    const res = await lmsService.uploadUrl(u);
    return { url: res.data.url };
  } catch {
    return { url: u };
  }
}

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Не вдалося прочитати файл'));
    reader.readAsDataURL(file);
  });
}

export const api = {
  aiRewrite,
  uploadFile,
  uploadFileProgress,
  uploadUrl,
};

export default api;
